import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

const LandingPage = () => {
	const navigate = useNavigate();
	const [scrollY, setScrollY] = useState(0);
	const [isVisible, setIsVisible] = useState({});

	useEffect(() => {
		const handleScroll = () => setScrollY(window.scrollY);
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
					}
				});
			},
			{ threshold: 0.1 }
		);

		const elements = document.querySelectorAll('[data-animate]');
		elements.forEach((el) => observer.observe(el));

		return () => observer.disconnect();
	}, []);

	const featuredFeatures = [
		{
			icon: (
				<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
				</svg>
			),
			title: 'Document Storage',
			description: 'Secure cloud storage for all startup documents',
		},
		{
			icon: (
				<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
				</svg>
			),
			title: 'Investor Sharing',
			description: 'Share financials and reports with investors',
		},
		{
			icon: (
				<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
				</svg>
			),
			title: 'Pitch Events',
			description: 'Connect with investors through organized events',
		},
		{
			icon: (
				<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
				</svg>
			),
			title: 'Enterprise Security',
			description: 'Bank-level encryption for your data',
		},
	];

	const features = [
		{
			icon: (
				<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
				</svg>
			),
			title: 'Secure Document Storage',
			description: 'Store all your startup documents in one secure place. Financial reports, monthly revenues, legal documents, and more - all organized and protected.',
		},
		{
			icon: (
				<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
				</svg>
			),
			title: 'Share with Investors',
			description: 'Easily share monthly revenues, financial statements, and key documents with investors. Control who sees what with granular permissions.',
		},
		{
			icon: (
				<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
				</svg>
			),
			title: 'Pitch Deck Management',
			description: 'Upload your pitch decks and share them with investor networks. Perfect for fundraising rounds and investor presentations.',
		},
		{
			icon: (
				<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
				</svg>
			),
			title: 'Pitch Events',
			description: 'Organize and participate in pitch events. Connect with multiple investors at once, showcase your startup, and manage event documents.',
		},
		{
			icon: (
				<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
				</svg>
			),
			title: 'Investor Network Access',
			description: 'Investors can browse all startup profiles, view documents, financials, and pitch decks. Discover investment opportunities in one place.',
		},
		{
			icon: (
				<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
				</svg>
			),
			title: 'Enterprise Security',
			description: 'Bank-level encryption and access controls protect your sensitive financial data and business documents. Built for startups and investors who value security.',
		},
	];

	return (
		<div className="min-h-screen bg-white">
			{/* Navigation */}
			<nav className="fixed top-0 left-0 right-0 z-50 bg-white backdrop-blur-md border-b border-gray-200 transition-all duration-300" style={{ boxShadow: scrollY > 10 ? '0 2px 8px rgba(0, 0, 0, 0.08)' : 'none' }}>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex justify-between items-center h-16">
						<div className="flex items-center">
							<Logo showText={true} width={40} height={40} />
						</div>
						<div className="hidden md:flex items-center space-x-8">
							<a href="#features" className="text-gray-700 hover:text-gray-900 transition-colors font-medium">Features</a>
							<a href="#benefits" className="text-gray-700 hover:text-gray-900 transition-colors font-medium">Benefits</a>
							<Link
								to="/login"
								className="bg-[#E42527] text-white px-6 py-2.5 rounded-md font-semibold hover:bg-[#C41E20] transition-all shadow-sm hover:shadow-md"
							>
								Sign In
							</Link>
						</div>
						<div className="md:hidden">
							<Link
								to="/login"
								className="bg-[#E42527] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#C41E20] transition-all"
							>
								Sign In
							</Link>
						</div>
					</div>
				</div>
			</nav>

			{/* Hero Section - Zoho Style Split Layout */}
			<section className="relative pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
				<div className="max-w-7xl mx-auto">
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
						{/* Left Side - Main Content */}
						<div className="lg:col-span-2 pt-8">
							<div data-animate id="hero" className={`transition-all duration-1000 ${isVisible.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
								<h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
									Your startup's work,<br />
									<span className="text-gray-900">powered by secure</span><br />
									<span className="text-gray-900">document sharing</span>
								</h1>
								<p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-2xl leading-relaxed">
									A unique and powerful platform to transform the way startups and investors connect. Designed for businesses of all sizes, built by a company that values your privacy.
								</p>
								<div className="flex flex-col sm:flex-row gap-4">
									<Link
										to="/login"
										className="group inline-flex items-center justify-center bg-[#E42527] text-white px-8 py-4 rounded-md font-bold text-lg hover:bg-[#C41E20] transition-all shadow-md hover:shadow-lg"
									>
										<span>GET STARTED FOR FREE</span>
										<svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
										</svg>
									</Link>
								</div>
							</div>
						</div>

						{/* Right Side - Logo and Features */}
						<div className="lg:col-span-1 flex flex-col items-center space-y-8">
							<div data-animate id="hero-logo" className={`transition-all duration-1000 delay-300 ${isVisible['hero-logo'] ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}>
								<Logo width={280} height={280} showText={false} className="drop-shadow-2xl" />
							</div>

							<div data-animate id="featured-card" className={`w-full bg-white rounded-lg border border-gray-200 shadow-sm p-6 transition-all duration-1000 ${isVisible['featured-card'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
								<h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">FEATURED FEATURES</h3>
								<div className="space-y-4">
									{featuredFeatures.map((feature, index) => (
										<div key={index} className="group cursor-pointer">
											<div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
												<div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white">
													{feature.icon}
												</div>
												<div className="flex-1 min-w-0">
													<h4 className="text-base font-semibold text-gray-900 mb-1 group-hover:text-[#E42527] transition-colors">
														{feature.title}
													</h4>
													<p className="text-sm text-gray-600 leading-snug">
														{feature.description}
													</p>
												</div>
												<svg className="w-5 h-5 text-gray-400 group-hover:text-[#E42527] transition-colors flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
												</svg>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Trust Metrics Section - Bold Blue */}
			<section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F4C81]">
				<div className="max-w-7xl mx-auto">
					<div className="grid grid-cols-2 md:grid-cols-4 gap-8">
						<div data-animate id="metric-1" className={`text-center transition-all duration-1000 ${isVisible['metric-1'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
							<div className="text-5xl md:text-6xl font-bold text-white mb-2">500+</div>
							<div className="text-lg text-blue-200">Startups</div>
							<div className="text-sm text-blue-300">Connected</div>
						</div>
						<div data-animate id="metric-2" className={`text-center transition-all duration-1000 delay-100 ${isVisible['metric-2'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
							<div className="text-5xl md:text-6xl font-bold text-white mb-2">200+</div>
							<div className="text-lg text-blue-200">Investors</div>
							<div className="text-sm text-blue-300">Active</div>
						</div>
						<div data-animate id="metric-3" className={`text-center transition-all duration-1000 delay-200 ${isVisible['metric-3'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
							<div className="text-5xl md:text-6xl font-bold text-white mb-2">10K+</div>
							<div className="text-lg text-blue-200">Documents</div>
							<div className="text-sm text-blue-300">Shared Securely</div>
						</div>
						<div data-animate id="metric-4" className={`text-center transition-all duration-1000 delay-300 ${isVisible['metric-4'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
							<div className="text-5xl md:text-6xl font-bold text-white mb-2">50+</div>
							<div className="text-lg text-blue-200">Pitch Events</div>
							<div className="text-sm text-blue-300">Organized</div>
						</div>
					</div>
				</div>
			</section>

			{/* Yellow Highlight Section */}
			<section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#FFD700]">
				<div className="max-w-5xl mx-auto">
					<div data-animate id="highlight" className={`text-center transition-all duration-1000 ${isVisible.highlight ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
						<div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 rounded-xl mb-6">
							<svg className="w-8 h-8 text-[#FFD700]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
							</svg>
						</div>
						<h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
							The all-in-one platform
						</h2>
						<p className="text-xl md:text-2xl text-gray-800 mb-2">
							<strong>iVault</strong>
						</p>
						<p className="text-lg text-gray-700">
							The operating system for startup-investor relations
						</p>
					</div>
				</div>
			</section>

			{/* Features Section */}
			<section id="features" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
				<div className="max-w-7xl mx-auto">
					<div data-animate id="features-header" className={`text-center mb-16 transition-all duration-1000 ${isVisible['features-header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
						<h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
							Everything you need to connect & grow
						</h2>
						<p className="text-xl text-gray-600 max-w-3xl mx-auto">
							Designed specifically for startups and investors. Store documents, share financials, manage pitch decks, and connect through organized events.
						</p>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
						{features.map((feature, index) => (
							<div
								key={index}
								data-animate
								id={`feature-${index}`}
								className={`group bg-white p-8 rounded-lg border border-gray-200 hover:border-gray-300 transition-all duration-300 hover:shadow-lg ${isVisible[`feature-${index}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
								style={{ transitionDelay: `${index * 50}ms` }}
							>
								<div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white mb-6">
									{feature.icon}
								</div>
								<h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
								<p className="text-gray-600 leading-relaxed">{feature.description}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Benefits Section */}
			<section id="benefits" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
				<div className="max-w-7xl mx-auto">
					<div data-animate id="benefits-header" className={`text-center mb-16 transition-all duration-1000 ${isVisible['benefits-header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
						<h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
							Why choose iVault
						</h2>
						<p className="text-xl text-gray-600 max-w-2xl mx-auto">
							Be part of the next generation of document management. Join early adopters who are revolutionizing how startups and investors connect.
						</p>
					</div>
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
						<div data-animate id="benefits-content" className={`space-y-8 transition-all duration-1000 ${isVisible['benefits-content'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
							<div className="flex items-start space-x-4">
								<div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
									<svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
									</svg>
								</div>
								<div>
									<h3 className="text-2xl font-bold text-gray-900 mb-2">For Startups: Streamline Fundraising</h3>
									<p className="text-gray-600">Store all your documents in one place, share monthly revenues and financials with investors, upload pitch decks, and connect with investor networks through organized pitch events.</p>
								</div>
							</div>
							<div className="flex items-start space-x-4">
								<div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
									<svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
									</svg>
								</div>
								<div>
									<h3 className="text-2xl font-bold text-gray-900 mb-2">For Investors: Discover Opportunities</h3>
									<p className="text-gray-600">Browse all startup profiles, view documents, financials, and pitch decks in one platform. Access comprehensive startup details to make informed investment decisions.</p>
								</div>
							</div>
							<div className="flex items-start space-x-4">
								<div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
									<svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
									</svg>
								</div>
								<div>
									<h3 className="text-2xl font-bold text-gray-900 mb-2">Secure & Organized</h3>
									<p className="text-gray-600">Enterprise-grade security protects your sensitive financial data. Organize documents by category, control access permissions, and maintain professional presentation standards.</p>
								</div>
							</div>
						</div>
						<div data-animate id="benefits-visual" className={`transition-all duration-1000 ${isVisible['benefits-visual'] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
							<div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
								<div className="space-y-6">
									<div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
										<div className="flex items-center space-x-3">
											<div className="w-12 h-12 bg-[#0F4C81] rounded-lg flex items-center justify-center">
												<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
												</svg>
											</div>
											<div>
												<div className="font-semibold text-gray-900">Early Access</div>
												<div className="text-sm text-gray-600">Join the launch</div>
											</div>
										</div>
										<div className="text-2xl font-bold text-[#0F4C81]">Now</div>
									</div>
									<div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-100">
										<div className="flex items-center space-x-3">
											<div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
												<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
												</svg>
											</div>
											<div>
												<div className="font-semibold text-gray-900">Free Trial</div>
												<div className="text-sm text-gray-600">14 days, no credit card</div>
											</div>
										</div>
										<div className="text-2xl font-bold text-green-600">100%</div>
									</div>
									<div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
										<div className="flex items-center space-x-3">
											<div className="w-12 h-12 bg-[#FFD700] rounded-lg flex items-center justify-center">
												<svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
												</svg>
											</div>
											<div>
												<div className="font-semibold text-gray-900">Enterprise Security</div>
												<div className="text-sm text-gray-600">From day one</div>
											</div>
										</div>
										<div className="text-2xl font-bold text-gray-900">✓</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* CTA Section */}
			<section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F4C81]">
				<div className="max-w-4xl mx-auto text-center">
					<div data-animate id="cta" className={`transition-all duration-1000 ${isVisible.cta ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
						<h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
							Ready to transform your startup-investor connections?
						</h2>
						<p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
							Whether you're a startup looking to share documents and pitch decks, or an investor seeking comprehensive startup details, iVault is your platform.
						</p>
						<div className="flex flex-col sm:flex-row gap-4 justify-center">
							<Link
								to="/login"
								className="inline-flex items-center justify-center bg-[#E42527] text-white px-8 py-4 rounded-md font-bold text-lg hover:bg-[#C41E20] transition-all shadow-lg hover:shadow-xl"
							>
								<span>GET STARTED FOR FREE</span>
								<svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
								</svg>
							</Link>
						</div>
					</div>
				</div>
			</section>

			{/* Footer */}
			<footer className="bg-gray-900 text-gray-400 py-12 px-4 sm:px-6 lg:px-8">
				<div className="max-w-7xl mx-auto">
					<div className="grid grid-cols-1 md:grid-cols-4 gap-8">
						<div>
							<Logo />
							<p className="mt-4 text-sm">
								Your secure digital vault for document management and investor relations.
							</p>
						</div>
						<div>
							<h3 className="text-white font-semibold mb-4">Product</h3>
							<ul className="space-y-2 text-sm">
								<li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
								<li><a href="#benefits" className="hover:text-white transition-colors">Benefits</a></li>
								<li><Link to="/login" className="hover:text-white transition-colors">Sign In</Link></li>
							</ul>
						</div>
						<div>
							<h3 className="text-white font-semibold mb-4">Company</h3>
							<ul className="space-y-2 text-sm">
								<li><a href="#" className="hover:text-white transition-colors">About</a></li>
								<li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
								<li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
								<li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
							</ul>
						</div>
						<div>
							<h3 className="text-white font-semibold mb-4">Legal</h3>
							<ul className="space-y-2 text-sm">
								<li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
								<li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
							</ul>
						</div>
					</div>
					<div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm">
						<p>&copy; {new Date().getFullYear()} iVault - Your Digital Vault. All rights reserved.</p>
					</div>
				</div>
			</footer>
		</div>
	);
};

export default LandingPage;
